import type { RowData } from "@tanstack/react-table";
import { displayAccountLabel } from "@common/helpers/accounting";
import { getAccountNameWithLast4 } from "@common/helpers/financialAccount";
import { ResolvedInstitutionIcon } from "@design-system/InstitutionIcon/ResolvedInstitutionIcon";
import {
    isFinancialAccountValue,
    isLedgerAccountValue,
    toDisplayText,
} from "./cellValues";
import { EmptyCellValue } from "./EmptyCellValue";
import { SearchableText } from "./SearchableText";
import type { DataGridCellProps } from "./types";
import styles from "./cellContent.module.scss";

interface AccountCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    /** Prefix chart-of-accounts names with their code: `1000 - Cash`. */
    showCode: boolean;
}

/**
 * Account column cell for both account shapes: a chart-of-accounts account
 * (name, optionally with its code) or a bank or provider account
 * (institution logo plus name with its last digits). Strings show as text.
 */
export const AccountCell = <TData extends RowData>({
    context,
    meta,
    showCode,
}: Readonly<AccountCellProps<TData>>) => {
    const value = context.getValue();
    if (isFinancialAccountValue(value)) {
        const name = getAccountNameWithLast4(value, 2);
        if (name === "") {
            return <EmptyCellValue empty={meta.empty} />;
        }
        return (
            <span className={styles.inline}>
                <ResolvedInstitutionIcon
                    size="xs"
                    icon={value.institutionIcon}
                />
                <SearchableText
                    text={name}
                    overflow={meta.overflow}
                    table={context.table}
                />
            </span>
        );
    }
    const label = isLedgerAccountValue(value)
        ? displayAccountLabel(value, showCode)
        : toDisplayText(value);
    return label ? (
        <SearchableText
            text={label}
            overflow={meta.overflow}
            table={context.table}
        />
    ) : (
        <EmptyCellValue empty={meta.empty} />
    );
};
